import { useCallback } from "react"

import {
  useEstimateFromMonthlyCosts,
  useEstimateFromMonthlyUsage,
  useEstimateFromAnnualUsage,
} from "~/api/hooks/genability/useIntelligentBaseline"
import type { IntelligentBaselineResponseDto } from "~/api/services/genability/genability.types"

import type { MonthData, DisplayUnit } from "../components/types"

interface EstimateSourceData {
  type: `annual` | `bill` | `default`
  annualUsage?: number
  billAmount?: number
  billStartMonth?: number
  billEndMonth?: number
}

interface UseEnergyEstimationReturn {
  isLoading: boolean
  estimateFromAnnual: (
    annualUsage: number,
  ) => Promise<IntelligentBaselineResponseDto>
  estimateFromBill: (
    billAmount: number,
    billStartMonth: number,
    billEndMonth?: number,
  ) => Promise<IntelligentBaselineResponseDto>
  estimateFromMonthlyInputs: (
    monthlyData: MonthData[],
    displayUnit: DisplayUnit,
  ) => Promise<IntelligentBaselineResponseDto>
  mutations: {
    monthlyCosts: ReturnType<typeof useEstimateFromMonthlyCosts>
    monthlyUsage: ReturnType<typeof useEstimateFromMonthlyUsage>
    annualUsage: ReturnType<typeof useEstimateFromAnnualUsage>
  }
}

export const useEnergyEstimation = (
  accountId: string | undefined,
  setEstimateSourceData: (data: EstimateSourceData) => void,
): UseEnergyEstimationReturn => {
  const estimateFromMonthlyCosts = useEstimateFromMonthlyCosts()
  const estimateFromMonthlyUsage = useEstimateFromMonthlyUsage()
  const estimateFromAnnualUsage = useEstimateFromAnnualUsage()

  const isLoading =
    estimateFromMonthlyCosts.isLoading ||
    estimateFromMonthlyUsage.isLoading ||
    estimateFromAnnualUsage.isLoading

  const estimateFromAnnual = useCallback(
    async (annualUsage: number) => {
      if (!accountId) {
        throw new Error(`No Genability account ID found`)
      }

      const result = await estimateFromAnnualUsage.mutateAsync({
        accountId,
        annualUsage,
      })

      setEstimateSourceData({
        type: `annual`,
        annualUsage,
      })

      return result
    },
    [accountId, estimateFromAnnualUsage, setEstimateSourceData],
  )

  const estimateFromBill = useCallback(
    async (
      billAmount: number,
      billStartMonth: number,
      billEndMonth?: number,
    ) => {
      if (!accountId) {
        throw new Error(`No Genability account ID found`)
      }

      const result = await estimateFromMonthlyCosts.mutateAsync({
        accountId,
        monthlyBills: [
          {
            month: billStartMonth,
            amount: billAmount,
          },
        ],
      })

      setEstimateSourceData({
        type: `bill`,
        billAmount,
        billStartMonth,
        billEndMonth: billEndMonth ?? billStartMonth,
      })

      return result
    },
    [accountId, estimateFromMonthlyCosts, setEstimateSourceData],
  )

  const estimateFromMonthlyInputs = useCallback(
    async (monthlyData: MonthData[], displayUnit: DisplayUnit) => {
      if (!accountId) {
        throw new Error(`No Genability account ID found`)
      }

      const userInputs = monthlyData
        .filter((month) => month.source === `user`)
        .map((month) => ({
          month: month.index,
          ...(displayUnit === `$`
            ? { amount: month.costValue || 0 }
            : { usage: month.kwhValue || 0 }),
        }))

      if (displayUnit === `$`) {
        return await estimateFromMonthlyCosts.mutateAsync({
          accountId,
          monthlyBills: userInputs as { month: number; amount: number }[],
        })
      }
      return await estimateFromMonthlyUsage.mutateAsync({
        accountId,
        monthlyUsage: userInputs as { month: number; usage: number }[],
      })
    },
    [accountId, estimateFromMonthlyCosts, estimateFromMonthlyUsage],
  )

  return {
    isLoading,
    estimateFromAnnual,
    estimateFromBill,
    estimateFromMonthlyInputs,
    mutations: {
      monthlyCosts: estimateFromMonthlyCosts,
      monthlyUsage: estimateFromMonthlyUsage,
      annualUsage: estimateFromAnnualUsage,
    },
  }
}
